import React, { useState, useEffect, useMemo } from 'react';
import { X, Printer, Download, Search, Bus as BusIcon, Users, Sparkles } from 'lucide-react';

// Registered bus students grouped by PHYSICAL BUS number, with a "new this year"
// flag (no bus enrollment last year). A student on a different morning vs evening
// bus shows under both buses with a remark. Columns: name, class, bus address,
// parent phone. On-screen + A4 print (one section per bus).
const BusByBusReportModal = ({ isOpen, onClose, apiCall, selectedYear, currentTrimester, showError }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [academicYear, setAcademicYear] = useState(selectedYear);
  const [trimester, setTrimester] = useState(currentTrimester ? String(currentTrimester) : 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [newOnly, setNewOnly] = useState(false);

  const trimesterLabel = trimester === 'all' ? 'Whole year' : `Trimester ${trimester}`;

  const loadReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (academicYear) params.append('academicYear', academicYear);
      if (trimester && trimester !== 'all') params.append('trimester', trimester);
      const data = await apiCall(`/bus/reports/by-bus?${params.toString()}`);
      setReport(data);
    } catch (error) {
      showError('Failed to load bus roster: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, academicYear, trimester]);

  // Apply search + "new only" per bus, dropping buses left empty.
  const buses = useMemo(() => {
    if (!report?.buses) return [];
    const term = searchTerm.trim().toLowerCase();
    return report.buses
      .map((b) => {
        const students = b.students.filter((s) => {
          if (newOnly && !s.isNew) return false;
          if (!term) return true;
          return (
            (s.studentName || '').toLowerCase().includes(term) ||
            (s.class || '').toLowerCase().includes(term) ||
            (s.phone || '').toLowerCase().includes(term) ||
            (s.address || '').toLowerCase().includes(term)
          );
        });
        return { ...b, students };
      })
      .filter((b) => b.students.length > 0);
  }, [report, searchTerm, newOnly]);

  const totalShown = useMemo(() => buses.reduce((n, b) => n + b.students.length, 0), [buses]);
  const totalNew = useMemo(() => buses.reduce((n, b) => n + b.students.filter((s) => s.isNew).length, 0), [buses]);

  const handlePrint = () => {
    const printContent = document.getElementById('by-bus-report-print');
    if (!printContent) return;
    const winPrint = window.open('', '', 'width=1000,height=700');
    winPrint.document.write(`
      <html>
        <head>
          <title>Bus Roster by Bus - ${academicYear || ''}</title>
          <style>
            @page { size: A4; margin: 14mm; }
            body { font-family: Arial, sans-serif; color: #111; }
            h1 { text-align: center; margin: 0 0 4px; font-size: 20px; }
            .sub { text-align: center; color: #555; font-size: 12px; margin-bottom: 16px; }
            .bus-section { page-break-inside: avoid; margin-bottom: 22px; }
            .bus-title { font-size: 15px; font-weight: bold; background: #f3f4f6; border: 1px solid #ddd;
                         padding: 6px 10px; border-radius: 4px; margin-bottom: 6px; }
            .bus-title .meta { font-weight: normal; color: #555; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; font-size: 12px; vertical-align: top; }
            th { background: #f9fafb; }
            .new { color: #047857; font-weight: bold; }
            .idx { width: 26px; color: #888; }
            .page-break { page-break-after: always; }
          </style>
        </head>
        <body>
          <h1>Bus Roster — by Bus</h1>
          <div class="sub">Academic Year ${academicYear || ''} · ${trimesterLabel} · ${totalShown} student ride(s) · ${totalNew} new</div>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    winPrint.document.close();
    winPrint.focus();
    setTimeout(() => { winPrint.print(); winPrint.close(); }, 300);
  };

  // Excel export: a UTF-8 CSV (opens directly in Excel). BOM so Arabic renders,
  // and every field quoted since addresses/names contain commas. A Bus column
  // keeps the grouping; rows follow the same filters shown on screen.
  const handleExport = () => {
    const q = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const headers = ['Bus', '#', 'Student Name', 'Class', 'Bus Address', 'Phone', 'Remark', 'New'];
    const rows = [];
    buses.forEach((b) => {
      b.students.forEach((s, i) => {
        rows.push([`Bus #${b.busNumber}`, i + 1, s.studentName, s.class, s.address, s.phone, s.remark, s.isNew ? 'NEW' : '']);
      });
    });
    const csv = '﻿' + [headers, ...rows].map((r) => r.map(q).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bus-roster-by-bus-${academicYear || 'year'}-${trimester === 'all' ? 'wholeyear' : 'T' + trimester}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Bus Roster — by Bus</h2>
            <p className="text-sm text-gray-600 mt-1">Registered students grouped by bus, with new riders flagged</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              disabled={loading || buses.length === 0}
            >
              <Download size={18} />
              Export Excel
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              disabled={loading || buses.length === 0}
            >
              <Printer size={18} />
              Print
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="px-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
              <input
                type="text"
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="26-27"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trimester</label>
              <select
                value={trimester}
                onChange={(e) => setTrimester(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="all">Whole year</option>
                <option value="1">Trimester 1</option>
                <option value="2">Trimester 2</option>
                <option value="3">Trimester 3</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Search size={14} className="inline mr-1" /> Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Name, class, address, phone..."
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input type="checkbox" checked={newOnly} onChange={(e) => setNewOnly(e.target.checked)} />
                New riders only
              </label>
            </div>
          </div>

          {!loading && report && (
            <div className="flex flex-wrap gap-3 mb-2 text-sm">
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-50 text-indigo-800 rounded-lg font-medium">
                {trimesterLabel}
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-800 rounded-lg">
                <BusIcon size={14} /> {buses.length} bus(es)
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-lg">
                <Users size={14} /> {totalShown} ride(s)
              </span>
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-800 rounded-lg">
                <Sparkles size={14} /> {totalNew} new
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : buses.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No registered bus students for {academicYear}.</div>
          ) : (
            <div id="by-bus-report-print">
              {buses.map((b, bi) => (
                <div key={b.busNumber} className="bus-section mb-6">
                  <div className="bus-title text-base font-bold bg-gray-100 border border-gray-200 rounded px-3 py-2 mb-2">
                    🚌 Bus #{b.busNumber}{' '}
                    <span className="meta font-normal text-gray-500 text-sm">
                      · {b.students.length} student(s) · {b.students.filter((s) => s.isNew).length} new
                    </span>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="idx">#</th>
                        <th>Student Name</th>
                        <th>Class</th>
                        <th>Bus Address</th>
                        <th>Phone</th>
                        <th>Remark</th>
                        <th>New?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b.students.map((s, i) => (
                        <tr key={`${s.studentId}-${i}`}>
                          <td className="idx">{i + 1}</td>
                          <td>{s.studentName}</td>
                          <td>{s.class || '—'}</td>
                          <td>{s.address || '—'}</td>
                          <td>{s.phone || '—'}</td>
                          <td>{s.remark || '—'}</td>
                          <td>{s.isNew ? <span className="new">NEW</span> : ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bi < buses.length - 1 && <div className="page-break" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusByBusReportModal;
